"use client";

import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useMedia } from "react-use";
import { useTranslations } from "next-intl";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FLOATING_SHADOW } from "@/components/search/station-hub/hub-ui";
import { useCurrency } from "@/contexts/CurrencyContext";
import {
  CAR_CLASSES,
  CAR_SEAT_STEPS,
  carClassOf,
  carSpecsOf,
  matchesCarAxes,
  type CarAxes,
  type CarClass,
} from "@/lib/mobility/car-rental";
import { cn } from "@/lib/utils";
import type { SupportedCurrency } from "@/lib/utils/currency";
import type { Product } from "@/types";

type Axis = "class" | "seats";

/**
 * 렌터카 필터 축 알약 — 차급 · 인승(10-12 확정. 명소·인수 장소 알약은 렌터카에서 숨긴다).
 * 허브 AxisPills 와 같은 규칙: 피커 안 선택은 임시이고 "N대 보기"를 눌러야 반영된다.
 * sm 이상은 알약 아래 팝오버, 미만은 바텀 시트.
 *
 * 차급 피커 타일에는 그 차급의 대수와 최저 1일가를 함께 보여 준다(Klook 상단 차급 카드 대신).
 */
export function CarAxisPills({
  products,
  axes,
  onChange,
  floating = false,
}: {
  /** 축 필터를 걸기 전의 렌터카 목록 — 타일 숫자·최저가는 이것으로 센다 */
  products: Product[];
  axes: CarAxes;
  onChange: (axes: CarAxes) => void;
  floating?: boolean;
}) {
  const t = useTranslations("search.car");
  const isWide = useMedia("(min-width: 640px)", false);
  // 금액은 기본 상품 카드(PriceDisplay showCurrencyCode)와 같은 통화 코드 표기 — "KRW 7,250"
  const { convertFromSource, formatWithCodeFromSource } = useCurrency();
  const [openAxis, setOpenAxis] = useState<Axis | null>(null);
  const [draft, setDraft] = useState<CarAxes>(axes);

  const classStats = useMemo(() => {
    const stats = new Map<CarClass, { count: number; cheapest: Product | null }>();
    for (const product of products) {
      const key = carClassOf(product, carSpecsOf(product.specifications));
      const entry = stats.get(key) ?? { count: 0, cheapest: null };
      entry.count += 1;
      const value = (p: Product) =>
        convertFromSource(p.price_per_day || 0, (p.currency || "USD") as SupportedCurrency);
      if ((product.price_per_day ?? 0) > 0 && (!entry.cheapest || value(product) < value(entry.cheapest))) {
        entry.cheapest = product;
      }
      stats.set(key, entry);
    }
    return stats;
  }, [products, convertFromSource]);

  const maxSeats = useMemo(
    () => Math.max(0, ...products.map((p) => carSpecsOf(p.specifications).seats ?? 0)),
    [products],
  );
  const seatSteps = CAR_SEAT_STEPS.filter((n) => n <= maxSeats);
  const presentClasses = CAR_CLASSES.filter((key) => classStats.has(key));

  const openPicker = (axis: Axis) => {
    setDraft(axes);
    setOpenAxis(axis);
  };
  const apply = () => {
    onChange(draft);
    setOpenAxis(null);
  };
  const draftCount = products.filter((p) => matchesCarAxes(p, draft)).length;

  const pillLabel = (axis: Axis) => {
    if (axis === "class") {
      if (axes.classes.length === 0) return { label: t("axisClass"), extra: 0 };
      return { label: t(`class.${axes.classes[0]}`), extra: axes.classes.length - 1 };
    }
    return { label: axes.minSeats ? t("seatsMin", { n: axes.minSeats }) : t("axisSeats"), extra: 0 };
  };
  const isActive = (axis: Axis) => (axis === "class" ? axes.classes.length > 0 : axes.minSeats !== null);

  const tile = (key: string, label: string, sub: string, selected: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex min-w-0 flex-col items-start gap-1 rounded-xl px-3 py-3 text-left transition",
        selected ? "border-2 border-gray-900 bg-white" : "border border-gray-200 bg-white hover:bg-gray-50",
      )}
    >
      <span className={cn("text-[13px] text-gray-900", selected ? "font-semibold" : "font-medium")}>{label}</span>
      <span className="text-[12px] leading-snug text-gray-500 tabular-nums">{sub}</span>
    </button>
  );

  const renderBody = (axis: Axis) =>
    axis === "class" ? (
      <div className="grid grid-cols-2 gap-2">
        {presentClasses.map((key) => {
          const stat = classStats.get(key)!;
          const cheapest = stat.cheapest;
          const sub = cheapest
            ? t("classSummary", {
                count: stat.count,
                price: formatWithCodeFromSource(cheapest.price_per_day || 0, (cheapest.currency || "USD") as SupportedCurrency),
              })
            : t("seatsCount", { count: stat.count });
          const selected = draft.classes.includes(key);
          return tile(key, t(`class.${key}`), sub, selected, () =>
            setDraft((prev) => ({
              ...prev,
              classes: selected ? prev.classes.filter((c) => c !== key) : [...prev.classes, key],
            })),
          );
        })}
      </div>
    ) : (
      <div className="grid grid-cols-2 gap-2">
        {[null, ...seatSteps].map((n) => {
          const count = products.filter((p) => matchesCarAxes(p, { classes: [], minSeats: n })).length;
          return tile(
            String(n ?? "any"),
            n === null ? t("seatsAny") : t("seatsMin", { n }),
            t("seatsCount", { count }),
            draft.minSeats === n,
            () => setDraft((prev) => ({ ...prev, minSeats: n })),
          );
        })}
      </div>
    );

  const renderPanel = (axis: Axis) => (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-5 pb-3 pt-4">
        <h2 className="text-base font-semibold text-gray-900">{axis === "class" ? t("axisClass") : t("axisSeats")}</h2>
        <button
          type="button"
          onClick={() =>
            setDraft((prev) => (axis === "class" ? { ...prev, classes: [] } : { ...prev, minSeats: null }))
          }
          className="text-[13px] text-gray-500 hover:text-gray-900"
        >
          {t("reset")}
        </button>
      </div>
      <div className="px-5 pb-4">{renderBody(axis)}</div>
      <div className="px-5 pb-5 pt-1">
        <button
          type="button"
          onClick={apply}
          className="h-12 w-full rounded-xl bg-orange-500 text-[15px] font-semibold text-white hover:bg-orange-600"
        >
          {t("showResults", { count: draftCount })}
        </button>
      </div>
    </div>
  );

  const axesToShow: Axis[] = [
    ...(presentClasses.length > 1 || axes.classes.length > 0 ? (["class"] as const) : []),
    ...(seatSteps.length > 1 || axes.minSeats !== null ? (["seats"] as const) : []),
  ];

  return (
    <>
      {axesToShow.map((axis) => {
        const { label, extra } = pillLabel(axis);
        const active = isActive(axis);
        const pill = (
          <button
            type="button"
            onClick={() => openPicker(axis)}
            aria-expanded={openAxis === axis}
            className={cn(
              // 허브 축 알약과 같은 치수 — 모바일 34px/13px, 데스크톱 40px/14px
              "inline-flex h-8.5 shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full bg-white pl-3.5 pr-2.5 text-[13px] font-medium text-gray-700 transition hover:bg-gray-50",
              "mapShow:h-10 mapShow:pl-4 mapShow:pr-3 mapShow:text-sm",
              floating ? `pointer-events-auto border border-black/10 ${FLOATING_SHADOW}` : "border border-gray-300",
              active && "font-semibold",
            )}
          >
            {active ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" /> : null}
            <span>{label}</span>
            {extra > 0 ? (
              <span className="rounded-full bg-gray-100 px-1.5 text-[11px] font-medium leading-4 text-gray-500">
                +{extra}
              </span>
            ) : null}
            <ChevronDown className="h-3.5 w-3.5 text-gray-400 mapShow:h-4 mapShow:w-4" />
          </button>
        );

        if (isWide) {
          return (
            <Popover key={axis} open={openAxis === axis} onOpenChange={(next) => setOpenAxis(next ? axis : null)}>
              <PopoverTrigger asChild>{pill}</PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={8}
                className="w-[360px] rounded-2xl border-gray-100 bg-white p-0 shadow-lg"
              >
                {renderPanel(axis)}
              </PopoverContent>
            </Popover>
          );
        }
        return (
          <span key={axis} className="contents">
            {pill}
            <Drawer open={openAxis === axis} onOpenChange={(next) => setOpenAxis(next ? axis : null)}>
              <DrawerContent className="h-auto max-h-none bg-white p-0">
                <VisuallyHidden>
                  <DrawerTitle>{axis === "class" ? t("axisClass") : t("axisSeats")}</DrawerTitle>
                </VisuallyHidden>
                {renderPanel(axis)}
              </DrawerContent>
            </Drawer>
          </span>
        );
      })}
    </>
  );
}
